import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SignaturePad } from "@/pages/OnboardingSign";

const STYLES = ["Dancing Script", "Great Vibes", "Caveat"];

async function typedPng(text, font) {
  await document.fonts.load(`64px "${font}"`);
  const c = document.createElement("canvas");
  c.width = 640; c.height = 180;
  const ctx = c.getContext("2d");
  let size = 72;
  do { ctx.font = `${size}px "${font}"`; size -= 4; } while (ctx.measureText(text).width > 600 && size > 24);
  ctx.fillStyle = "#0F1B3D"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(text, 320, 95);
  return c.toDataURL("image/png");
}

// DocuSign-style "Adopt your signature" dialog: choose a typed style or draw.
export function AdoptSignature({ open, onOpenChange, defaultName, onAdopt }) {
  const [name, setName] = useState(defaultName || "");
  const [tab, setTab] = useState("style");
  const [style, setStyle] = useState(STYLES[0]);
  const [err, setErr] = useState("");
  const pad = useRef(null);

  async function adopt() {
    setErr("");
    if (name.trim().length < 2) { setErr("Please enter your full name."); return; }
    if (tab === "draw" && !pad.current?.hasDrawn()) { setErr("Please draw your signature."); return; }
    const png = tab === "draw" ? pad.current.toPng() : await typedPng(name.trim(), style);
    onAdopt({ name: name.trim(), png });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="fbsign-adopt-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-strong">Adopt your signature</DialogTitle>
          <DialogDescription>Confirm your name and choose how your signature will look.</DialogDescription>
        </DialogHeader>
        <label className="text-xs text-[var(--ink-2)] block">Full name</label>
        <input data-testid="fbsign-adopt-name" value={name} onChange={(e) => setName(e.target.value)} className="input text-sm w-full" />
        <div className="flex gap-4 border-b border-[var(--line)] text-sm">
          {[["style", "Choose style"], ["draw", "Draw"]].map(([k, l]) => (
            <button key={k} data-testid={`fbsign-adopt-tab-${k}`} onClick={() => setTab(k)}
              className={`pb-2 -mb-px border-b-2 transition-colors ${tab === k ? "border-[var(--navy)] text-[var(--navy)] font-semibold" : "border-transparent text-[var(--ink-2)]"}`}>{l}</button>
          ))}
        </div>
        {tab === "style" ? (
          <div className="space-y-2" data-testid="fbsign-adopt-styles">
            {STYLES.map((s) => (
              <label key={s} data-testid={`fbsign-adopt-style-${s.replace(/\s/g, "-").toLowerCase()}`}
                className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${style === s ? "border-[var(--navy)] bg-[var(--bg-2)]" : "border-[var(--line)] hover:border-[var(--navy)]"}`}>
                <input type="radio" checked={style === s} onChange={() => setStyle(s)} />
                <span style={{ fontFamily: `"${s}", cursive`, fontSize: 30, color: "#0F1B3D" }} className="truncate">{name || "Your name"}</span>
              </label>
            ))}
          </div>
        ) : <SignaturePad ref={pad} testid="fbsign-signature-canvas" />}
        <p className="text-[11px] text-[var(--ink-2)]">By selecting Adopt and sign, I agree that this signature is the electronic representation of my signature for all purposes when I use it on documents.</p>
        {err && <div className="p-2 bg-[var(--err-bg)] border border-[var(--err)] text-[var(--err)] rounded text-sm" data-testid="fbsign-adopt-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="btn-ghost text-sm !py-2 !px-4" data-testid="fbsign-adopt-cancel">Cancel</button>
          <button onClick={adopt} className="btn-primary text-sm !py-2 !px-4" data-testid="fbsign-adopt-submit">Adopt and sign</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
